import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Rating,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  CheckCircleOutline as CheckIcon,
  Comment as CommentIcon,
  Edit as RequestEditIcon,
  ExpandMore as ExpandMoreIcon,
  Flag as FlagIcon,
  Refresh as RefreshIcon,
  Cancel as RejectIcon,
  EmojiEvents as ExperienceIcon,
  LocalShipping as ProcessIcon,
  PhotoCamera as PhotoCameraIcon,
  Star as StarIcon,
  ThumbUp as ThumbUpIcon,
  Build as BuildIcon,
  WarningAmber as WarningIcon,
  ArrowBackIosNew as PrevPhotoIcon,
  ArrowForwardIos as NextPhotoIcon,
  Translate as TranslateIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import reviewAdminService from '../../services/reviewAdminService';
import api from '../../services/api';

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'warning' },
  changes_requested: { label: 'Modifs demandées', color: 'info' },
  approved: { label: 'Approuvée', color: 'success' },
  rejected: { label: 'Rejetée', color: 'error' }
};

const STATUS_OPTIONS = [
  { value: 'pending', label: 'En attente' },
  { value: 'changes_requested', label: 'Modifs demandées' },
  { value: 'approved', label: 'Approuvées' },
  { value: 'rejected', label: 'Rejetées' },
  { value: 'reported', label: '🚩 Signalées' },
  { value: 'all', label: 'Toutes' }
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Plus récentes' },
  { value: 'oldest', label: 'Plus anciennes' },
  { value: 'recently_flagged', label: 'Signalements récents' },
  { value: 'most_flagged', label: 'Les plus signalées' },
  { value: 'highest_rating', label: 'Meilleures notes' },
  { value: 'lowest_rating', label: 'Moins bonnes notes' }
];

const CATEGORY_OPTIONS = [
  { value: 'routine_review', label: 'Revue de routine' },
  { value: 'content_policy', label: 'Politique de contenu' },
  { value: 'quality_issue', label: 'Problème de qualité' },
  { value: 'user_report', label: 'Signalement utilisateur' },
  { value: 'automated_flag', label: 'Signalement automatisé' },
  { value: 'safety_violation', label: 'Violation sécurité' },
  { value: 'fraud_suspicion', label: 'Suspicion de fraude' }
];

const CATEGORY_VALUES = new Set(CATEGORY_OPTIONS.map((option) => option.value));

const ACTION_LABELS = {
  approve: 'Approuver',
  reject: 'Rejeter',
  request_edit: 'Demander des modifications'
};

const defaultModerationState = {
  open: false,
  review: null,
  action: null,
  notes: '',
  followUpRequired: false,
  followUpDate: '',
  severity: 'low',
  category: 'routine_review'
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return format(parsed, 'Pp', { locale: fr });
};

const getStatusConfig = (status) => STATUS_CONFIG[status] || { label: 'Non défini', color: 'default' };

const buildErrorMessage = (error) => {
  const apiData = error?.response?.data;
  return (
    apiData?.error ||
    apiData?.message ||
    apiData?.msg ||
    error?.message ||
    'Une erreur est survenue. Veuillez réessayer.'
  );
};

const ReviewModerationPage = () => {
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    status: 'pending',
    flaggedOnly: false,
    sort: 'newest',
    resubmittedOnly: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [moderationDialog, setModerationDialog] = useState(defaultModerationState);
  const [submittingModeration, setSubmittingModeration] = useState(false);
  const [expandedReviewId, setExpandedReviewId] = useState(null);
  const [photosCache, setPhotosCache] = useState({});
  const [photosLoadingId, setPhotosLoadingId] = useState(null);
  const [photoErrors, setPhotoErrors] = useState({});
  const [photoCarouselIndex, setPhotoCarouselIndex] = useState({});
  
  // États pour la traduction automatique
  const [translating, setTranslating] = useState(false);
  const [translatedNotes, setTranslatedNotes] = useState('');
  const [translationResult, setTranslationResult] = useState(null);
  const [showTranslation, setShowTranslation] = useState(false);

  const limitOptions = useMemo(() => [10, 20, 50], []);

  const loadReviews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { reviews: queueReviews, pagination: queuePagination } = await reviewAdminService.fetchModerationQueue({
        page,
        limit: rowsPerPage,
        status: filters.status,
        flaggedOnly: filters.flaggedOnly,
        sort: filters.sort,
        resubmittedOnly: filters.resubmittedOnly
      });

      setReviews(queueReviews || []);
      setPagination(queuePagination || { total: 0, totalPages: 1 });
    } catch (err) {
      const message = buildErrorMessage(err);
      setError(message);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [filters.flaggedOnly, filters.resubmittedOnly, filters.sort, filters.status, page, rowsPerPage]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const handleRefresh = async () => {
    await loadReviews();
    // Recharger les photos du panneau actuellement ouvert
    if (expandedReviewId) {
      await loadReviewPhotos(expandedReviewId);
    }
  };

  const loadReviewPhotos = useCallback(async (reviewId) => {
    try {
      setPhotosLoadingId(reviewId);
      setPhotoErrors((prev) => ({ ...prev, [reviewId]: null }));
      const response = await api.get(`/reviews/reservations/${reviewId}/photos`, {
        params: { _t: Date.now() }
      });
      const photos = response?.data?.data?.equipment_photos || [];
      setPhotosCache((prev) => ({ ...prev, [reviewId]: photos }));
      setPhotoCarouselIndex((prev) => {
        if (!photos.length) {
          if (prev[reviewId] === undefined) {
            return prev;
          }
          const next = { ...prev };
          delete next[reviewId];
          return next;
        }

        const current = prev[reviewId] ?? 0;
        const adjusted = Math.min(current, photos.length - 1);
        if (current === adjusted && prev[reviewId] !== undefined) {
          return prev;
        }
        return { ...prev, [reviewId]: adjusted };
      });
    } catch (err) {
      const message = buildErrorMessage(err);
      setPhotoErrors((prev) => ({ ...prev, [reviewId]: message }));
    } finally {
      setPhotosLoadingId(null);
    }
  }, []);

  const toggleReviewDetails = async (review) => {
    if (expandedReviewId === review.id) {
      setExpandedReviewId(null);
      return;
    }

    setExpandedReviewId(review.id);
    if (photosCache[review.id]?.length) {
      setPhotoCarouselIndex((prev) => {
        if (prev[review.id] !== undefined) {
          return prev;
        }
        return { ...prev, [review.id]: 0 };
      });
    }
    if (!photosCache[review.id]) {
      await loadReviewPhotos(review.id);
    }
  };

  const handlePhotoNavigation = useCallback((reviewId, direction) => {
    setPhotoCarouselIndex((prev) => {
      const photos = photosCache[reviewId] || [];
      if (!photos.length) {
        return prev;
      }

      const current = prev[reviewId] ?? 0;
      const delta = direction === 'prev' ? -1 : 1;
      const nextIndex = (current + delta + photos.length) % photos.length;

      if (nextIndex === current) {
        return prev;
      }

      return { ...prev, [reviewId]: nextIndex };
    });
  }, [photosCache]);

  const handlePhotoSelect = useCallback((reviewId, index) => {
    setPhotoCarouselIndex((prev) => {
      const photos = photosCache[reviewId] || [];
      if (!photos.length || index < 0 || index >= photos.length) {
        return prev;
      }

      const current = prev[reviewId] ?? 0;
      if (current === index) {
        return prev;
      }

      return { ...prev, [reviewId]: index };
    });
  }, [photosCache]);

  const openModerationDialog = (review, action) => {
    setModerationDialog({
      ...defaultModerationState,
      open: true,
      review,
      action
    });
  };

  const closeModerationDialog = () => {
    setModerationDialog(defaultModerationState);
    setTranslatedNotes('');
    setTranslationResult(null);
    setShowTranslation(false);
  };

  const handleTranslateNotes = async () => {
    const { review, notes } = moderationDialog;
    
    if (!notes || notes.trim().length < 10) {
      setFeedback({
        type: 'warning',
        message: 'Les notes doivent contenir au moins 10 caractères pour être traduites.'
      });
      return;
    }

    setTranslating(true);
    try {
      const response = await api.post(`/reviews/reservations/${review.id}/translate-moderation`, {
        notes: notes.trim()
      });

      if (response.data.success) {
        const translation = response.data.translation;
        setTranslationResult(translation);

        if (translation.needsTranslation) {
          if (translation.translatedNotes) {
            setTranslatedNotes(translation.translatedNotes);
            setShowTranslation(true);
            setFeedback({
              type: 'success',
              message: `Traduction générée en ${translation.targetLanguage}`
            });
          } else {
            setFeedback({
              type: 'error',
              message: response.data.message || 'Erreur lors de la traduction'
            });
          }
        } else {
          setFeedback({
            type: 'info',
            message: response.data.message
          });
        }
      } else {
        setFeedback({
          type: 'error',
          message: response.data.message || 'Erreur lors de la traduction'
        });
      }
    } catch (error) {
      console.error('Error translating moderation notes:', error);
      if (error.response?.status === 503) {
        setFeedback({
          type: 'error',
          message: 'Service de traduction indisponible'
        });
      } else {
        setFeedback({
          type: 'error',
          message: error.response?.data?.message || 'Erreur lors de la traduction'
        });
      }
    } finally {
      setTranslating(false);
    }
  };

  const handleUseTranslation = () => {
    if (translatedNotes) {
      handleModerationChange('notes', translatedNotes);
      setShowTranslation(false);
      setFeedback({
        type: 'success',
        message: 'Traduction appliquée'
      });
    }
  };

  const handleModerationChange = (key, value) => {
    setModerationDialog((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const handleModerationSubmit = async () => {
    if (!moderationDialog.review || !moderationDialog.action) {
      return;
    }

    // Le commentaire est obligatoire pour toute action de modération
    if (!moderationDialog.notes || moderationDialog.notes.trim().length === 0) {
      setFeedback({
        type: 'error',
        message: 'Un commentaire est obligatoire pour effectuer cette action de modération.'
      });
      return;
    }

    setSubmittingModeration(true);
    setFeedback(null);

    const normalizedCategory = CATEGORY_VALUES.has(moderationDialog.category)
      ? moderationDialog.category
      : 'routine_review';

    const payload = {
      action: moderationDialog.action,
      notes: moderationDialog.notes.trim(),
      follow_up_required: moderationDialog.followUpRequired,
      follow_up_date: moderationDialog.followUpRequired && moderationDialog.followUpDate
        ? moderationDialog.followUpDate
        : undefined,
      severity: moderationDialog.severity,
      category: normalizedCategory
    };

    try {
      const response = await reviewAdminService.moderateReview(moderationDialog.review.id, payload);
      const reviewId = moderationDialog.review.id;
      const actionDone = moderationDialog.action;
      setFeedback({ type: 'success', message: response?.message || 'Action appliquée avec succès.' });
      closeModerationDialog();
      // Mise à jour optimiste immédiate → préserve le focus / scroll
      const statusMap = { approve: 'approved', reject: 'rejected', request_edit: 'changes_requested' };
      const newStatus = statusMap[actionDone];
      if (newStatus) {
        setReviews(prev => prev.map(r =>
          r.id === reviewId ? { ...r, moderation_status: newStatus, moderator_notes: payload.notes } : r
        ));
      }
      // Rechargement en arrière-plan pour synchroniser avec le serveur
      loadReviews();
    } catch (err) {
      setFeedback({ type: 'error', message: buildErrorMessage(err) });
    } finally {
      setSubmittingModeration(false);
    }
  };

  const isActionDisabled = (review, action) => {
    const status = review?.moderation_status;
    if (!status) return false;
    if (action === 'approve') return status === 'approved';
    if (action === 'reject') return status === 'rejected';
    if (action === 'request_edit') return status === 'changes_requested';
    return false;
  };

  const renderDetailedReview = (review) => {
    if (!review) {
      return null;
    }

    const photos = photosCache[review.id] || [];
    const isPhotosLoading = photosLoadingId === review.id;
    const photoError = photoErrors[review.id];
    const rawPhotoIndex = photoCarouselIndex[review.id] ?? 0;
    const normalizedPhotoIndex = photos.length ? Math.min(rawPhotoIndex, photos.length - 1) : 0;
    const resolvePhotoSrc = (item) => {
      if (!item) {
        return '';
      }
      if (typeof item === 'string') {
        return item;
      }
      return item.url || item.src || item.path || '';
    };
    const activePhotoSrc = resolvePhotoSrc(photos[normalizedPhotoIndex]);
    const overallRating = Number(review.overall_rating);
    const hasOverallRating = !Number.isNaN(overallRating);

    const ratingSections = [
      {
        key: 'equipment',
        icon: <BuildIcon color="primary" fontSize="small" />,
        label: 'Équipement',
        ratingKey: 'equipment_rating',
        subsections: [
          { ratingKey: 'equipment_quality_rating', label: 'Qualité', commentKey: 'equipment_quality_comment' },
          { ratingKey: 'equipment_cleanliness_rating', label: 'Propreté', commentKey: 'equipment_cleanliness_comment' },
          { ratingKey: 'equipment_state_rating', label: 'État général', commentKey: 'equipment_state_comment' }
        ]
      },
      {
        key: 'process',
        icon: <ProcessIcon color="success" fontSize="small" />,
        label: 'Processus',
        ratingKey: 'process_rating',
        subsections: [
          { ratingKey: 'process_pickup_rating', label: 'Retrait', commentKey: 'process_pickup_comment' },
          { ratingKey: 'process_return_rating', label: 'Retour', commentKey: 'process_return_comment' },
          { ratingKey: 'process_communication_rating', label: 'Communication', commentKey: 'process_communication_comment' }
        ]
      },
      {
        key: 'experience',
        icon: <ExperienceIcon color="info" fontSize="small" />,
        label: 'Expérience',
        ratingKey: 'experience_rating',
        subsections: [
          { ratingKey: 'experience_value_rating', label: 'Rapport qualité/prix', commentKey: 'experience_value_comment' }
        ]
      }
    ];

    const renderSubRatings = (section) => (
      <Stack spacing={1.25} mt={1.5}>
        {section.subsections.map((item) => {
          const value = review[item.ratingKey];
          if (value === null || value === undefined) {
            return null;
          }
          const numericValue = Number(value);
          if (Number.isNaN(numericValue)) {
            return null;
          }

          return (
            <Box key={item.ratingKey}>
              <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  {item.label}
                </Typography>
                <Rating value={numericValue} readOnly size="small" precision={0.5} />
                <Typography variant="caption" color="text.secondary">
                  {numericValue.toFixed(1)}/5
                </Typography>
              </Stack>
              {item.commentKey && review[item.commentKey] ? (
                <Typography variant="body2" color="text.secondary" sx={{ pl: 1, borderLeft: '3px solid rgba(0,0,0,0.08)' }}>
                  {review[item.commentKey]}
                </Typography>
              ) : null}
            </Box>
          );
        })}
      </Stack>
    );

    return (
      <Stack spacing={3} sx={{ mt: 2 }}>
        {review.resubmitted_at ? (
          <Alert severity="info" icon={<RequestEditIcon fontSize="inherit" />}>
            <Typography variant="body2" fontWeight={600}>
              Avis resoumis le {formatDateTime(review.resubmitted_at)}
            </Typography>
            {review.resubmission_count ? (
              <Typography variant="body2">
                Nombre total de resoumissions : {review.resubmission_count}
              </Typography>
            ) : null}
          </Alert>
        ) : null}

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <StarIcon color="warning" />
                <Typography variant="h6" fontWeight={600}>
                  Note globale
                </Typography>
              </Stack>
              {hasOverallRating ? (
                <>
                  <Rating value={overallRating} readOnly precision={0.5} />
                  <Typography variant="h5" fontWeight={600} color="warning.dark">
                    {overallRating.toFixed(1)}/5
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Note indisponible
                </Typography>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Évalué le {formatDateTime(review.created_at)}
            </Typography>
          </Stack>
          {review.overall_comment ? (
            <Typography variant="body1" sx={{ mt: 1.5, fontStyle: 'italic', color: 'text.secondary' }}>
              « {review.overall_comment} »
            </Typography>
          ) : null}
        </Paper>

        {ratingSections.map((section) => {
          const value = review[section.ratingKey];
          if (value === null || value === undefined) {
            return null;
          }
          const numericValue = Number(value);
          if (Number.isNaN(numericValue)) {
            return null;
          }

          return (
            <Paper key={section.key} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                {section.icon}
                <Typography variant="subtitle1" fontWeight={600}>
                  {section.label}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1} mt={1}>
                <Rating value={numericValue} readOnly size="small" precision={0.5} />
                <Typography variant="body2" fontWeight={600}>
                  {numericValue.toFixed(1)}/5
                </Typography>
              </Stack>
              {renderSubRatings(section)}
            </Paper>
          );
        })}

        {(review.positive_tags?.length || review.negative_tags?.length) ? (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1.5}>
              {review.positive_tags?.length ? (
                <Box>
                  <Typography variant="subtitle2" color="success.main" gutterBottom>
                    Points positifs
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {review.positive_tags.map((tag) => (
                      <Chip key={`positive-${tag}`} label={tag} size="small" color="success" variant="outlined" />
                    ))}
                  </Stack>
                </Box>
              ) : null}
              {review.negative_tags?.length ? (
                <Box>
                  <Typography variant="subtitle2" color="warning.main" gutterBottom>
                    Points à améliorer
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {review.negative_tags.map((tag) => (
                      <Chip key={`negative-${tag}`} label={tag} size="small" color="warning" variant="outlined" />
                    ))}
                  </Stack>
                </Box>
              ) : null}
            </Stack>
          </Paper>
        ) : null}

        {review.would_recommend !== null && review.would_recommend !== undefined ? (
          <Alert severity={review.would_recommend ? 'success' : 'warning'} icon={review.would_recommend ? <ThumbUpIcon fontSize="inherit" /> : undefined}>
            <Typography variant="body2" fontWeight={600}>
              {review.would_recommend ? 'Le loueur recommande cette expérience.' : 'Le loueur ne recommande pas cette expérience.'}
            </Typography>
          </Alert>
        ) : null}

        {review.owner_response ? (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <CheckIcon fontSize="small" color="info" />
              <Typography variant="subtitle2" fontWeight={600}>
                Réponse du propriétaire
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {review.owner_response}
            </Typography>
            {review.owner_response_date ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Répondu le {formatDateTime(review.owner_response_date)}
              </Typography>
            ) : null}
          </Paper>
        ) : null}

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <PhotoCameraIcon color="primary" />
            <Typography variant="subtitle2" fontWeight={600}>
              Photos associées
            </Typography>
          </Stack>
          {isPhotosLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Chargement des photos…
              </Typography>
            </Stack>
          ) : photoError ? (
            <Alert severity="warning">{photoError}</Alert>
          ) : photos.length ? (
            <Stack spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  maxWidth: 640,
                  height: { xs: 220, sm: 320 },
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: 'grey.100'
                }}
              >
                {activePhotoSrc ? (
                  <Box
                    component="img"
                    src={activePhotoSrc}
                    alt={`Photo ${normalizedPhotoIndex + 1}`}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                ) : (
                  <Stack
                    sx={{ width: '100%', height: '100%' }}
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                  >
                    <PhotoCameraIcon color="disabled" />
                    <Typography variant="body2" color="text.secondary">
                      Photo indisponible
                    </Typography>
                  </Stack>
                )}

                {photos.length > 1 ? (
                  <>
                    <IconButton
                      size="small"
                      onClick={() => handlePhotoNavigation(review.id, 'prev')}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: 12,
                        transform: 'translateY(-50%)',
                        backgroundColor: 'rgba(0,0,0,0.45)',
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.65)'
                        }
                      }}
                    >
                      <PrevPhotoIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handlePhotoNavigation(review.id, 'next')}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        right: 12,
                        transform: 'translateY(-50%)',
                        backgroundColor: 'rgba(0,0,0,0.45)',
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.65)'
                        }
                      }}
                    >
                      <NextPhotoIcon fontSize="small" />
                    </IconButton>
                  </>
                ) : null}

                {photos.length ? (
                  <Typography
                    variant="caption"
                    sx={{
                      position: 'absolute',
                      left: 12,
                      bottom: 12,
                      backgroundColor: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      borderRadius: 1,
                      px: 1,
                      py: 0.25,
                      fontWeight: 600
                    }}
                  >
                    {`Photo ${normalizedPhotoIndex + 1}/${photos.length}`}
                  </Typography>
                ) : null}
              </Box>

              {photos.length > 1 ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  {photos.map((photo, index) => {
                    const src = resolvePhotoSrc(photo);
                    if (!src) {
                      return null;
                    }
                    const isActive = index === normalizedPhotoIndex;
                    return (
                      <Box
                        key={`review-${review.id}-indicator-${index}`}
                        component="button"
                        type="button"
                        onClick={() => handlePhotoSelect(review.id, index)}
                        aria-label={`Afficher la photo ${index + 1}`}
                        sx={{
                          all: 'unset',
                          width: isActive ? 16 : 12,
                          height: isActive ? 16 : 12,
                          borderRadius: '50%',
                          backgroundColor: isActive ? 'primary.main' : 'grey.400',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: isActive ? '0 0 0 2px rgba(25,118,210,0.3)' : 'none'
                        }}
                      />
                    );
                  })}
                </Stack>
              ) : null}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Aucune photo disponible pour cet avis.
            </Typography>
          )}
        </Paper>
      </Stack>
    );
  };

  const renderReviewCard = (review) => {
    const statusConfig = getStatusConfig(review?.moderation_status || (review?.moderated ? 'approved' : 'pending'));
    const isExpanded = expandedReviewId === review.id;
    const toggleLabel = isExpanded ? 'Masquer les détails' : 'Afficher les détails complets';

    return (
      <Paper key={review.id} elevation={2} sx={{ p: 2, borderRadius: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CommentIcon fontSize="small" />
                  {review?.listing?.title || 'Listing inconnu'}
                </Typography>
                <Chip label={`Note ${review?.overall_rating || '—'}/5`} color="primary" size="small" variant="outlined" />
                <Chip label={statusConfig.label} color={statusConfig.color} size="small" />
                {review.flagged && (
                  <Chip
                    icon={<FlagIcon />}
                    label="Signalé"
                    color="error"
                    size="small"
                    variant="outlined"
                  />
                )}
                {review.resubmission_count > 0 && (
                  <Tooltip
                    title={
                      review.resubmitted_at
                        ? `Dernière resoumission le ${formatDateTime(review.resubmitted_at)}`
                        : 'Resoumis et en attente'
                    }
                  >
                    <Chip
                      label={`Resoumis x${review.resubmission_count}`}
                      color="secondary"
                      size="small"
                      variant="outlined"
                    />
                  </Tooltip>
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Avis créé le {formatDateTime(review?.created_at)} par {review?.reviewer?.name || review?.reviewer?.email || 'Utilisateur inconnu'}
              </Typography>
              {review.flagged && review.flag_reason && (
                <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                  <WarningIcon fontSize="inherit" sx={{ mr: 0.5 }} /> Motif signalement : {review.flag_reason}
                </Typography>
              )}
            </Box>
            
            {/* Alert de signalement avec détails */}
            {review.reportInfo && review.reportInfo.report_count > 0 && (
              <Alert severity="error" icon={<WarningIcon />} sx={{ width: '100%' }}>
                <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                  ⚠️ Cet avis a été signalé {review.reportInfo.report_count} fois
                </Typography>
                <Typography variant="body2" paragraph>
                  Dernier signalement: {review.reportInfo.last_reported_at && format(new Date(review.reportInfo.last_reported_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                </Typography>
                
                {/* Liste de tous les signalements */}
                {review.reportInfo.details && review.reportInfo.details.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" fontWeight="600" display="block" gutterBottom>
                      Tous les signalements ({review.reportInfo.details.length}):
                    </Typography>
                    <Stack spacing={1} mt={1}>
                      {review.reportInfo.details.map((detail, index) => (
                        <Box 
                          key={detail.id || index} 
                          sx={{ 
                            bgcolor: 'error.50', 
                            p: 1.5, 
                            borderRadius: 1,
                            borderLeft: '3px solid',
                            borderColor: 'error.main'
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="start" spacing={1}>
                            <Box flex={1}>
                              <Typography variant="body2" fontWeight="500">
                                {detail.reason}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                Par: {detail.reporter?.name || detail.reporter?.email || 'Utilisateur'} • 
                                Le {format(new Date(detail.reported_at), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                              </Typography>
                            </Box>
                            <Chip 
                              label={`#${index + 1}`} 
                              size="small" 
                              color="error" 
                              variant="outlined"
                            />
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Alert>
            )}
            <Stack direction="row" spacing={1}>
              <Tooltip title={toggleLabel}>
                <IconButton
                  color="primary"
                  onClick={() => toggleReviewDetails(review)}
                  sx={{
                    transition: 'transform 0.2s ease',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                >
                  <ExpandMoreIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={ACTION_LABELS.approve}>
                <span>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<ApproveIcon />}
                    onClick={() => openModerationDialog(review, 'approve')}
                    disabled={isActionDisabled(review, 'approve')}
                  >
                    {ACTION_LABELS.approve}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={ACTION_LABELS.request_edit}>
                <span>
                  <Button
                    variant="outlined"
                    color="info"
                    size="small"
                    startIcon={<RequestEditIcon />}
                    onClick={() => openModerationDialog(review, 'request_edit')}
                    disabled={isActionDisabled(review, 'request_edit')}
                  >
                    {ACTION_LABELS.request_edit}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={ACTION_LABELS.reject}>
                <span>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<RejectIcon />}
                    onClick={() => openModerationDialog(review, 'reject')}
                    disabled={isActionDisabled(review, 'reject')}
                  >
                    {ACTION_LABELS.reject}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Commentaire principal
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {review?.overall_comment || 'N/A'}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2">Réservation</Typography>
              <Typography variant="body2" color="text.secondary">
                ID : {review?.reservation?.id || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Période : {formatDateTime(review?.reservation?.start_date)} → {formatDateTime(review?.reservation?.end_date)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2">Auteur</Typography>
              <Typography variant="body2" color="text.secondary">
                {review?.reviewer?.name || review?.reviewer?.email || 'Non renseigné'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Email : {review?.reviewer?.email || '—'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2">Modération</Typography>
              <Typography variant="body2" color="text.secondary">
                Modérateur : {review?.moderator?.name || review?.moderator?.email || 'Non attribué'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Dernière action : {formatDateTime(review?.moderated_at)}
              </Typography>
              {review?.resubmitted_at ? (
                <Typography variant="body2" color="text.secondary">
                  Dernière resoumission : {formatDateTime(review.resubmitted_at)}
                </Typography>
              ) : null}
              {review?.resubmission_count ? (
                <Typography variant="body2" color="text.secondary">
                  Nombre de resoumissions : {review.resubmission_count}
                </Typography>
              ) : null}
            </Grid>
          </Grid>

          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            {renderDetailedReview(review)}
          </Collapse>
        </Stack>
      </Paper>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Gestion des avis & modération : Renter → Owner
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Surveillez les avis signalés, appliquez des décisions et suivez l\'activité de modération.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={loading}>
          Actualiser
        </Button>
      </Stack>

      {feedback && (
        <Alert severity={feedback.type} onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}

      <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>Statut</InputLabel>
            <Select
              value={filters.status}
              label="Statut"
              onChange={(event) => {
                setFilters((prev) => ({ ...prev, status: event.target.value }));
                setPage(1);
              }}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 220 }} size="small">
            <InputLabel>Tri</InputLabel>
            <Select
              value={filters.sort}
              label="Tri"
              onChange={(event) => {
                setFilters((prev) => ({ ...prev, sort: event.target.value }));
                setPage(1);
              }}
            >
              {SORT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={(
              <Switch
                checked={filters.flaggedOnly}
                onChange={(event) => {
                  setFilters((prev) => ({ ...prev, flaggedOnly: event.target.checked }));
                  setPage(1);
                }}
                color="error"
              />
            )}
            label="Signalements uniquement"
          />

          <FormControlLabel
            control={(
              <Switch
                checked={filters.resubmittedOnly}
                onChange={(event) => {
                  setFilters((prev) => ({ ...prev, resubmittedOnly: event.target.checked }));
                  setPage(1);
                }}
                color="secondary"
              />
            )}
            label="Resoumissions uniquement"
          />

          <FormControl sx={{ minWidth: 160 }} size="small">
            <InputLabel>Éléments par page</InputLabel>
            <Select
              value={rowsPerPage}
              label="Éléments par page"
              onChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(1);
              }}
            >
              {limitOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : reviews.length === 0 ? (
        <Alert severity="info">Aucun avis trouvé pour ces critères.</Alert>
      ) : (
        <Stack spacing={2}>
          {reviews.map((review) => renderReviewCard(review))}
        </Stack>
      )}

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {pagination.total || 0} avis au total
        </Typography>
        <Pagination
          count={pagination.totalPages || 1}
          page={page}
          onChange={(_, value) => setPage(value)}
          color="primary"
        />
      </Stack>
      <Dialog open={moderationDialog.open} onClose={closeModerationDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {moderationDialog.action ? ACTION_LABELS[moderationDialog.action] : 'Action de modération'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="warning">
              Cette action sera appliquée à l\'avis de {moderationDialog.review?.reviewer?.name || moderationDialog.review?.reviewer?.email || 'cet utilisateur'} concernant « {moderationDialog.review?.listing?.title || 'listing inconnu'} ».
            </Alert>

            <TextField
              label="Notes ou justification"
              multiline
              minRows={3}
              value={moderationDialog.notes}
              onChange={(event) => handleModerationChange('notes', event.target.value)}
              placeholder="Expliquez la décision prise ou les modifications demandées"
              required
              error={submittingModeration === false && moderationDialog.notes.trim().length === 0}
              helperText="Un commentaire détaillé est obligatoire pour cette action"
            />

            {/* Bouton de traduction */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                startIcon={translating ? <CircularProgress size={16} /> : <TranslateIcon />}
                onClick={handleTranslateNotes}
                disabled={translating || !moderationDialog.notes || moderationDialog.notes.trim().length < 10}
                size="small"
                variant="outlined"
              >
                {translating ? 'Traduction...' : 'Traduire automatiquement'}
              </Button>
              {showTranslation && translatedNotes && (
                <Chip
                  label="Traduction disponible"
                  color="success"
                  size="small"
                  onDelete={() => setShowTranslation(false)}
                />
              )}
            </Stack>

            {/* Afficher la traduction */}
            {showTranslation && translatedNotes && (
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.main' }}>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" color="success.dark">
                      Traduction proposée ({translationResult?.targetLanguage || 'langue détectée'})
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={handleUseTranslation}
                    >
                      Utiliser cette traduction
                    </Button>
                  </Stack>
                  <Typography variant="body2">
                    {translatedNotes}
                  </Typography>
                  {translationResult?.detectedLanguage && (
                    <Typography variant="caption" color="text.secondary">
                      Langue détectée : {translationResult.detectedLanguage}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
              <FormControl sx={{ minWidth: 160 }} size="small">
                <InputLabel>Sévérité</InputLabel>
                <Select
                  value={moderationDialog.severity}
                  label="Sévérité"
                  onChange={(event) => handleModerationChange('severity', event.target.value)}
                >
                  <MenuItem value="low">Faible</MenuItem>
                  <MenuItem value="medium">Moyenne</MenuItem>
                  <MenuItem value="high">Élevée</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 200 }} size="small">
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={CATEGORY_VALUES.has(moderationDialog.category) ? moderationDialog.category : 'routine_review'}
                  label="Catégorie"
                  onChange={(event) => handleModerationChange('category', event.target.value)}
                >
                  {CATEGORY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Choisissez la raison principale pour l'action de modération.</FormHelperText>
              </FormControl>
            </Stack>

            <FormControlLabel
              control={(
                <Switch
                  checked={moderationDialog.followUpRequired}
                  onChange={(event) => handleModerationChange('followUpRequired', event.target.checked)}
                />
              )}
              label="Planifier un suivi"
            />

            {moderationDialog.followUpRequired && (
              <TextField
                label="Date de suivi"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={moderationDialog.followUpDate}
                onChange={(event) => handleModerationChange('followUpDate', event.target.value)}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModerationDialog} disabled={submittingModeration}>
            Annuler
          </Button>
          <Button
            onClick={handleModerationSubmit}
            variant="contained"
            color="primary"
            disabled={submittingModeration || !moderationDialog.notes || moderationDialog.notes.trim().length === 0}
          >
            {submittingModeration ? 'Traitement…' : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewModerationPage;
